import { Clear, Search } from '@mui/icons-material';
import { Autocomplete, CircularProgress, IconButton } from '@mui/material';
import { Match, Switch, TextField } from '@side/pantry';
import { useRouter } from 'next/router';
import { type SyntheticEvent, useState } from 'react';
import {
  type SearchResult,
  type Totals,
  useSearchInteractions,
} from '@/views/Home/hooks';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { DropdownGroup } from './DropdownGroup';
import { DropdownOption } from './DropdownOption';
import { EmptyState } from './EmptyState';

export const SearchAllTransactionsSearchBox = () => {
  const trackEvent = useTrackHomePageAnalytics();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { push } = useRouter();
  const { setSearchValue, loading, searchValue, results, totals } =
    useSearchInteractions();

  const handleDropdownOptionClick = (
    _: SyntheticEvent<Element, Event>,
    value: SearchResult | null,
  ) => {
    if (value) {
      setIsRedirecting(true);
      trackEvent(TrackHomePageEvent.SearchResultSingleSelection, {
        interactionId: value.id,
        interactionType: value.type,
      });

      void push(`/${value.type}/${value.id}/cover-sheet`);
    }
  };

  const handleSearchInputChange = (
    _: SyntheticEvent<Element, Event>,
    newValue: string | null,
  ) => {
    setSearchValue(newValue ?? '');
    trackEvent(TrackHomePageEvent.SearchBarUpdate, {
      searchValue: newValue ?? '',
      task: 'modify_input',
    });
  };

  return (
    <Autocomplete
      onChange={handleDropdownOptionClick}
      inputValue={searchValue}
      open={
        dropdownOpen && Boolean(searchValue && (!loading || results.length))
      }
      onOpen={() => setDropdownOpen(true)}
      onClose={() => setDropdownOpen(false)}
      disabled={isRedirecting}
      onBlur={() => {
        trackEvent(TrackHomePageEvent.SearchBarUpdate, {
          searchValue: null,
          task: 'update_input',
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name="home-page-search"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <Search
                  sx={{
                    ml: 1,
                    color: isRedirecting ? 'action.disabled' : 'action.active',
                  }}
                />
              ),
              endAdornment: (
                <Switch>
                  <Match when={loading || isRedirecting}>
                    <CircularProgress sx={{ color: 'inherit' }} size={20} />
                  </Match>
                  <Match when={!!searchValue}>
                    <IconButton
                      onClick={() => {
                        trackEvent(TrackHomePageEvent.SearchBarClear, {
                          searchValue,
                        });
                        setSearchValue('');
                      }}
                      aria-label="clear"
                      sx={{ m: -1 }}
                    >
                      <Clear color="action" fontSize="small" />
                    </IconButton>
                  </Match>
                </Switch>
              ),
            },
          }}
          placeholder="Search by name or address"
          fullWidth
        />
      )}
      onInputChange={handleSearchInputChange}
      renderOption={(optionProps, option, state) => (
        <DropdownOption
          {...optionProps}
          option={option}
          key={option.id}
          searchTerm={searchValue}
          index={state.index}
        />
      )}
      renderGroup={(params) => (
        <DropdownGroup
          {...params}
          searchValue={searchValue}
          total={totals[params.group as keyof Totals]}
        />
      )}
      groupBy={(option) => option.type}
      filterOptions={(x) => x}
      options={results}
      autoComplete
      disablePortal
      clearOnBlur={false}
      noOptionsText={searchValue && !loading ? <EmptyState /> : ''}
      getOptionLabel={() => searchValue}
    />
  );
};
